import { trackEvent } from 'user-analytics';
import store from '@/store';

const EVENT_NAMES = {
  api: 'Funnel Error: API',
  screen: 'Funnel Error: Screen',
  payment_redirect: 'Funnel Error: Payment Redirect',
  purchase_step: 'Funnel Error: Purchase Step',
  lock_seats: 'Funnel Error: Lock Seats',
  bus_scheme: 'Funnel Error: Bus Scheme',
};

/**
 * Derives the current funnel step from the URL pathname.
 * @returns {string} One of 'payment', 'confirmation', 'checkout', 'search', 'home', or 'unknown'.
 */
function getCurrentFunnelStep() {
  const { pathname } = window.location;
  if (pathname.includes('/payment/')) return 'payment';
  if (pathname.includes('/purchase/') && pathname.includes('/complete')) return 'confirmation';
  if (pathname.includes('/purchase/')) return 'checkout';
  if (pathname.includes('/search')) return 'search';
  if (pathname === '/') return 'home';
  return 'unknown';
}

/**
 * Resolves a place id to its display name using the terminals/cities collections in the store.
 * @param {string} placeId - Terminal or city id from the search state.
 * @param {object} state - Full Redux state.
 * @returns {string|null} The place's city/name, or null if it can't be resolved.
 */
function resolvePlaceName(placeId, state) {
  if (!placeId) return null;
  const place = state.terminals?.[placeId] || state.cities?.[placeId];
  if (!place) return null;
  return place.cityName || place.name || null;
}

/**
 * Builds the current search context (origin, destination, dates) to enrich error events.
 * @returns {{origin: string|null, destination: string|null, roundTrip: boolean, departureDate: string|null}} Search context.
 */
function getSearchContext() {
  try {
    const state = store.getState();
    const { search } = state;
    const originId = search?.get?.('originId') || null;
    const destinationId = search?.get?.('destinationId') || null;
    const origin = resolvePlaceName(originId, state);
    const destination = resolvePlaceName(destinationId, state);
    const roundTrip = Boolean(search?.get?.('returnDate'));
    const departureDate = search?.get?.('departureDate') || null;
    return { origin, destination, roundTrip, departureDate };
  } catch {
    return { origin: null, destination: null, roundTrip: false, departureDate: null };
  }
}

/**
 * Sends a funnel error event to Mixpanel, enriching it with search context and defaults.
 * Never throws — tracking failures must not break the calling flow.
 * @param {string} category - Key into EVENT_NAMES identifying the error type.
 * @param {object} [properties] - Event properties to send, keyed by their Mixpanel display name.
 * @returns {void}
 */
function trackFunnelError(category, properties = {}) {
  try {
    const eventName = EVENT_NAMES[category];
    if (!eventName) return;

    const message = properties['Error Message'] || '';
    const searchContext = getSearchContext();
    trackEvent(eventName, {
      ...properties,
      'Error Category': category,
      'Error Code': properties['Error Code'] || null,
      'Error Message':
        typeof message === 'string' ? message.slice(0, 255) : String(message).slice(0, 255),
      'Funnel Step': properties['Funnel Step'] || getCurrentFunnelStep(),
      'Origin': properties.Origin || searchContext.origin,
      'Destination': properties.Destination || searchContext.destination,
      'Departure': properties.Departure || searchContext.departureDate,
      'Round Trip':
        properties['Round Trip'] != null ? properties['Round Trip'] : searchContext.roundTrip,
    });
  } catch {
    // Tracking should never break the main flow
  }
}

/**
 * Tracks a generic API error.
 * @param {object} [params] - Error details.
 * @param {number|string} [params.status] - HTTP status code or error code returned by the API.
 * @param {string} [params.message] - Error message.
 * @param {string} [params.url] - Identifier of the API call that failed.
 * @param {string} [params.funnel_step] - Funnel step the error occurred in; defaults to the current step.
 * @returns {void}
 */
export function trackApiError({ status, message, url, funnel_step: funnelStep } = {}) {
  trackFunnelError('api', {
    'Error Code': status !== undefined && status !== null ? String(status) : 'unknown',
    'Error Message': message || 'API error',
    'Api Url': url || null,
    'Funnel Step': funnelStep,
  });
}

/**
 * Tracks an unrecoverable error shown via the error fallback screen.
 * @param {object} [params] - Error details.
 * @param {string} [params.message] - Error message.
 * @returns {void}
 */
export function trackErrorScreen({ message } = {}) {
  trackFunnelError('screen', {
    'Error Message': message || 'Unrecoverable error',
  });
}

/**
 * Tracks a redirect to an external payment provider, both via the funnel error pipeline
 * and directly to Mixpanel with sendBeacon so the event survives page navigation.
 * @param {object} [params] - Redirect details.
 * @param {string} [params.provider] - Payment provider the user is being redirected to.
 * @param {boolean} [params.isExpected] - Whether the redirect is an expected part of the flow.
 * @returns {void}
 */
export function trackPaymentRedirect({ provider, isExpected = true } = {}) {
  const props = {
    'Provider': provider || 'unknown',
    'Is Expected': isExpected,
    'Funnel Step': 'payment',
  };
  trackFunnelError('payment_redirect', props);

  try {
    const mp = window.mixpanel;
    if (mp && typeof mp.track === 'function') {
      mp.track('Funnel Error: Payment Redirect', props, { transport: 'sendBeacon' });
    }
  } catch {
    // tracking should never break the main flow
  }
}

/**
 * Tracks an error that occurred during a purchase step (search, payment, etc.).
 * @param {object} [params] - Error details.
 * @param {number|string} [params.code] - Error code.
 * @param {string} [params.message] - Error message.
 * @param {string} [params.step] - Funnel step the error occurred in; defaults to the current step.
 * @param {string} [params.provider] - Payment/transporter provider involved, if any.
 * @returns {void}
 */
export function trackPurchaseStepError({ code, message, step, provider } = {}) {
  trackFunnelError('purchase_step', {
    'Error Code': code !== undefined && code !== null ? String(code) : null,
    'Error Message': message || 'Purchase step failed',
    'Funnel Step': step || getCurrentFunnelStep(),
    'Provider': provider || null,
  });
}

/**
 * Tracks an error that occurred while locking seats.
 * @param {object} [params] - Error details.
 * @param {number|string} [params.code] - Error code.
 * @param {string} [params.message] - Error message.
 * @returns {void}
 */
export function trackLockSeatsError({ code, message } = {}) {
  trackFunnelError('lock_seats', {
    'Error Code': code !== undefined && code !== null ? String(code) : null,
    'Error Message': message || 'Lock seats failed',
    'Funnel Step': 'seats',
  });
}

/**
 * Tracks an error that occurred while loading the bus seat scheme.
 * @param {object} [params] - Error details.
 * @param {number|string} [params.code] - Error code.
 * @param {string} [params.message] - Error message.
 * @param {string} [params.tripId] - Id of the trip whose scheme failed to load.
 * @returns {void}
 */
export function trackBusSchemeError({ code, message, tripId } = {}) {
  trackFunnelError('bus_scheme', {
    'Error Code': code !== undefined && code !== null ? String(code) : null,
    'Error Message': message || 'Bus scheme load failed',
    'Funnel Step': 'seats',
    'Trip Id': tripId || null,
  });
}
